import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Grid, TextField } from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers';
import { AccessTime } from '@mui/icons-material';

import Form from 'components/Form';
import MuiDialog from './components/MuiDialog';
import useNotification from 'hooks/useNotification';

import { IMedication } from '../PatientList/api/type';

interface Props {
  isUpdate: boolean;
  defaultValue: Partial<IMedication>;
  onClose: () => void;
  onSave: (data: IMedication) => void;
  onSearchNo: (no: number) => boolean;
}

const MedicationDialog = (props: Props) => {
  const { isUpdate, defaultValue, onClose, onSave, onSearchNo } = props;
  const [isSearched, setIsSearched] = useState(isUpdate);
  const [prescriptionTime, setPrescriptionTime] = useState<Date | null>(
    new Date(defaultValue.prescriptionTime!)
  );
  const [medicationTime, setMedicationTime] = useState<Date | null>(
    new Date(defaultValue.medicationTime!)
  );

  const { onSuccess, onRequired } = useNotification();
  const { handleSubmit, register, getValues, setValue } = useForm({
    defaultValues: defaultValue,
  });

  const onSubmit = (data: any) => {
    if (!isUpdate && onSearchNo(Number(data.ptMedicationNo)))
      return onRequired('REQUIRED.UNIQUE.MEDICATION.NO');
    if (!isSearched) return onRequired('REQUIRED.MEDICATION.ID');

    onSuccess('已儲存。');
    onSave({ ...data, prescriptionTime, medicationTime });
    onClose();
  };

  const onChangeMedicationId = (e: any) => {
    const { value } = e.target;
    setValue('medicationId', value);
    setValue('medicationName', '');
    setIsSearched(false);
  };

  return (
    <MuiDialog.SaveForm
      isOpen
      maxWidth="md"
      title="用藥紀錄資料"
      onClose={onClose}
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Grid container gap={2} sx={{ my: 2 }}>
        <Form.Item required label={'用藥序號'}>
          <TextField
            required
            size="small"
            type="number"
            {...register('ptMedicationNo')}
            InputProps={{ readOnly: isUpdate }}
          />
        </Form.Item>
        <Form.Item required label={'處方時間'}>
          <MobileTimePicker
            value={prescriptionTime}
            onChange={setPrescriptionTime}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                fullWidth={false}
                InputProps={{ endAdornment: <AccessTime /> }}
              />
            )}
          />
        </Form.Item>
        <Form.Item required label={'預定給藥時間'}>
          <MobileTimePicker
            value={medicationTime}
            onChange={setMedicationTime}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                fullWidth={false}
                InputProps={{ endAdornment: <AccessTime /> }}
              />
            )}
          />
        </Form.Item>
        <Grid item xs={12} />

        <Form.Item required label={'成分碼／通用名碼'}>
          <TextField
            size="small"
            {...register('medicationId')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'通用名'}>
          <TextField
            size="small"
            {...register('medicationName')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'給藥途徑'}>
          <TextField
            size="small"
            {...register('medicationMedicate')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'基本含量'}>
          <TextField
            size="small"
            {...register('medicationContent')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'基本單位'}>
          <TextField
            size="small"
            {...register('medicationMeasure')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Grid item xs={12} />
        <Form.Item required label={'給藥量'}>
          <TextField required size="small" {...register('medicationAmount')} />
        </Form.Item>
        <Form.Item required label={'次數'}>
          <TextField required size="small" {...register('medicationFreq')} />
        </Form.Item>

        <Grid item xs={12}>
          <Form.Item required fullWidth label={'詳細給藥方法'}>
            <TextField
              required
              fullWidth
              multiline
              minRows={5}
              size="small"
              {...register('medicationNote')}
            />
          </Form.Item>
        </Grid>
      </Grid>
    </MuiDialog.SaveForm>
  );
};

export default MedicationDialog;
